'use client';

import { useState, useEffect } from 'react';

function getFlightStatusColor(status: string): string {
  switch (status) {
    case 'ON_TIME':
      return 'bg-green-800 text-green-200';
    case 'CHECK_IN_CLOSING':
      return 'bg-blue-800 text-blue-200';
    case 'BOARDING_SOON':
      return 'bg-cyan-800 text-cyan-200';
    case 'BOARDING':
      return 'bg-green-700 text-green-100';
    case 'LAST_CALL':
      return 'bg-orange-800 text-orange-200';
    case 'GATE_CLOSING':
      return 'bg-red-700 text-red-100';
    case 'GATE_CLOSED':
      return 'bg-red-900 text-red-200';
    case 'DEPARTED':
      return 'bg-gray-800 text-gray-200';
    case 'DELAYED':
      return 'bg-yellow-800 text-yellow-200';
    case 'GATE_CHANGE':
      return 'bg-purple-800 text-purple-200';
    case 'CANCELLED':
      return 'bg-red-800 text-red-200';
    default:
      return 'bg-gray-800 text-gray-200';
  }
}

interface SessionData {
  phone: string;
  name: string | null;
  flightCode: string | null;
  gateLabel: string | null;
  currentLocation: string | null;
  lastSeen: string;
  stressStatus: 'RELAXED' | 'WARNING' | 'CRITICAL' | 'DEPARTED' | null;
  channel: string;
  flightStatus?: string;
  hasEvent?: boolean;
  passengerType: 'departing' | 'arriving' | 'greeter' | 'unknown';
  notificationsSent: string[];
  lastFlightCode: string | null;
}

export default function AdminDashboard() {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastNotificationCheck, setLastNotificationCheck] = useState<Date | null>(null);
  const [notificationsSentToday, setNotificationsSentToday] = useState(0);
  const [isCheckingNotifications, setIsCheckingNotifications] = useState(false);

  useEffect(() => {
    // Set initial time on client side only
    setCurrentTime(new Date());
    
    // Update current time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    // Fetch sessions initially and then every 10 seconds
    const fetchSessions = async () => {
      try {
        const response = await fetch('/api/sessions');
        const data = await response.json();
        setSessions(data.sessions || []);
        
        // Calculate total notifications sent today
        const totalNotifications = data.sessions?.reduce((total: number, session: SessionData) => {
          return total + (session.notificationsSent?.length || 0);
        }, 0) || 0;
        setNotificationsSentToday(totalNotifications);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        setIsLoading(false);
      }
    };

    fetchSessions();
    const interval = setInterval(fetchSessions, 10000);

    return () => clearInterval(interval);
  }, []);

  const checkNotifications = async () => {
    setIsCheckingNotifications(true);
    try {
      const response = await fetch('/api/proactive', { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        setLastNotificationCheck(new Date());
        setNotificationsSentToday(prev => prev + data.notificationsSent);
        
        // Refresh sessions to get updated notification data
        const sessionsResponse = await fetch('/api/sessions');
        const sessionsData = await sessionsResponse.json();
        setSessions(sessionsData.sessions || []);
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    } finally {
      setIsCheckingNotifications(false);
    }
  };

  const getStressStatusBadge = (status: string | null) => {
    if (!status) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No Flight</span>;
    }

    const colors = {
      RELAXED: 'bg-green-100 text-green-800',
      WARNING: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800',
      DEPARTED: 'bg-gray-100 text-gray-600'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[status as keyof typeof colors]}`}>
        {status}
      </span>
    );
  };

  const getPassengerTypeDisplay = (type: string) => {
    switch (type) {
      case 'departing':
        return '✈️ Departing';
      case 'arriving':
        return '🛬 Arriving';
      case 'greeter':
        return '👋 Waiting';
      case 'unknown':
      default:
        return '❓ Unknown';
    }
  };

  const getChannelBadge = (channel: string) => {
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        channel === 'whatsapp' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
      }`}>
        {channel}
      </span>
    );
  };

  const formatLastSeen = (lastSeen: string) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-airport-blue text-white">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="text-2xl">✈️</div>
            <div>
              <h1 className="text-xl font-bold">VIE Concierge</h1>
              <p className="text-blue-200 text-sm">Admin Dashboard</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="bg-blue-800 rounded-lg p-4">
              <h3 className="font-medium mb-2">Real Time</h3>
              <p className="text-2xl font-mono">
                {currentTime ? currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
              </p>
              <p className="text-blue-200 text-sm">
                {currentTime ? currentTime.toLocaleDateString() : '--'}
              </p>
            </div>
            
            <div className="bg-blue-800 rounded-lg p-4">
              <h3 className="font-medium mb-2">Active Sessions</h3>
              <p className="text-2xl font-bold">{sessions.length}</p>
            </div>
            
            <div className="bg-blue-800 rounded-lg p-4">
              <h3 className="font-medium mb-2">Proactive Notifications</h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-blue-200">Last Check</p>
                  <p className="text-sm font-mono">
                    {lastNotificationCheck 
                      ? lastNotificationCheck.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                      : 'Never'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-200">Sent Today</p>
                  <p className="text-lg font-bold">{notificationsSentToday}</p>
                </div>
                <button
                  onClick={checkNotifications}
                  disabled={isCheckingNotifications}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-900 disabled:opacity-50 text-white text-sm py-2 px-3 rounded transition-colors flex items-center justify-center gap-2"
                >
                  {isCheckingNotifications ? (
                    <>
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                      Checking...
                    </>
                  ) : (
                    <>
                      Check Now
                      <span className="text-xs">»</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Overview</h2>
          <p className="text-gray-600">Monitor all active passenger sessions across WhatsApp and web chat</p>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-airport-blue mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-4xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Sessions</h3>
            <p className="text-gray-600">No passengers are currently chatting with the concierge.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Passenger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flight Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flight Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stress Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Channel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Passenger Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Seen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session, index) => (
                    <tr key={session.phone} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {session.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {session.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        <div className="flex items-center gap-2">
                          {session.flightCode || '-'}
                          {session.hasEvent && (
                            <span className="text-orange-500" title="Flight has event (delay/gate change/cancellation)">
                              ⚠️
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {session.gateLabel || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {session.currentLocation || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.flightStatus ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getFlightStatusColor(session.flightStatus)}`}>
                            {session.flightStatus.replace('_', ' ')}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStressStatusBadge(session.stressStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getChannelBadge(session.channel)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPassengerTypeDisplay(session.passengerType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatLastSeen(session.lastSeen)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
